import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { TablePagination } from '../../components/ui/table-pagination';
import { toast } from 'sonner';
import axios from 'axios';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import AccountStatusBanner from '../../components/common/AccountStatusBanner';
import { 
  Wallet as WalletIcon, ArrowLeft,
  ArrowUpRight, ArrowDownLeft, TrendingUp, CreditCard, ChevronDown,
  Clock, CheckCircle, XCircle, Loader2, Eye, Package, X, Plus, Shield, Info
} from 'lucide-react';

const BACKEND_URL = window.location.origin;

export default function ProviderEarnings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  // Wallet state
  const [wallet, setWallet] = useState(null);
  const [isLoadingWallet, setIsLoadingWallet] = useState(true);
  const [lockedFunds, setLockedFunds] = useState(null);
  const [showLockedFundsInfo, setShowLockedFundsInfo] = useState(false);
  
  // Withdrawal settings
  const [withdrawalConfig, setWithdrawalConfig] = useState({
    minimum_amount: 500,
    maximum_amount: 50000,
    processing_fee: 0,
    processing_fee_type: 'fixed',
    enabled: true
  });
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  
  // Transaction detail
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  
  // Payout dialog
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [banks, setBanks] = useState([]);
  const [payoutData, setPayoutData] = useState({
    amount: '',
    payout_method: '',
    recipient_identifier: '',
    account_name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Top-up dialog for providers
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [isTopupLoading, setIsTopupLoading] = useState(false);
  const [walletSettings, setWalletSettings] = useState({ min_topup_amount: 100, topup_presets: [100, 500, 1000, 2000] });

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch wallet and locked funds
  const fetchWallet = useCallback(async () => {
    setIsLoadingWallet(true);
    try {
      // Fetch wallet balance (use provider-specific endpoint)
      const walletRes = await axios.get(`${BACKEND_URL}/api/provider/wallet`, { headers });
      setWallet(walletRes.data);
      
      // Fetch locked funds details
      const lockedRes = await axios.get(`${BACKEND_URL}/api/provider/wallet/locked-funds`, { headers });
      setLockedFunds(lockedRes.data);
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
      // Fallback to regular wallet endpoint
      try {
        const res = await axios.get(`${BACKEND_URL}/api/wallet`, { headers });
        setWallet(res.data);
      } catch (e) {
        console.error('Failed to fetch regular wallet:', e);
      }
    } finally {
      setIsLoadingWallet(false);
    }
  }, [token]);

  // Fetch transactions with pagination
  const fetchTransactions = useCallback(async () => {
    setIsLoadingTransactions(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/transactions?page=${page}&limit=${limit}`, { headers });
      setTransactions(res.data.transactions || []);
      setTransactionsTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [token, page, limit]);

  // Fetch transaction detail
  const fetchTransactionDetail = async (transactionId) => {
    setIsLoadingDetail(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/transactions/${transactionId}`, { headers });
      setSelectedTransaction(res.data);
      setShowDetailDialog(true);
    } catch (err) {
      toast.error('Failed to load transaction details');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Fetch banks
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/banks`)
      .then(res => {
        setBanks(res.data || []);
        if (res.data?.length > 0) {
          setPayoutData(prev => ({ ...prev, payout_method: res.data[0].id }));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch withdrawal config
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/config/withdrawal`)
      .then(res => {
        setWithdrawalConfig(res.data);
      })
      .catch(() => {});
    
    // Fetch wallet settings
    if (token) {
      axios.get(`${BACKEND_URL}/api/wallet-settings`, { headers })
        .then(res => {
          if (res.data?.min_topup_amount) setWalletSettings(res.data);
        })
        .catch(() => {});
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchWallet();
      fetchTransactions();
    }
  }, [token, fetchWallet, fetchTransactions]);

  // Handle return from PayMongo checkout (payment verification)
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const txnId = searchParams.get('txn_id');
    
    // Cancel any pending transactions when page loads (not completed = cancelled)
    const cancelPendingTransactions = async () => {
      if (!token) return;
      
      try {
        await axios.post(`${BACKEND_URL}/api/wallet/cancel-pending`, {}, { headers });
        // Refresh to show updated status
        fetchTransactions();
      } catch (error) {
        console.error('Failed to cancel pending transactions:', error);
      }
    };
    
    if (paymentStatus === 'success' && txnId) {
      // Verify the transaction with retries
      let retries = 0;
      const maxRetries = 5;
      const retryDelay = 2000;
      
      const verifyPayment = async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/wallet/topup/verify/${txnId}`, { headers });
          if (res.data.status === 'completed') {
            toast.success(`Payment successful! ₱${res.data.amount?.toLocaleString()} added to wallet.`);
            fetchWallet();
            fetchTransactions();
            return true;
          } else if (retries < maxRetries) {
            retries++;
            toast.info(`Verifying payment... (attempt ${retries}/${maxRetries})`);
            setTimeout(verifyPayment, retryDelay);
            return false;
          } else {
            toast.info('Payment is being processed. Your wallet will be updated shortly. Please refresh.');
            fetchWallet();
            fetchTransactions();
            return false;
          }
        } catch (error) {
          if (retries < maxRetries) {
            retries++;
            setTimeout(verifyPayment, retryDelay);
          } else {
            toast.info('Payment received. Please refresh to see your updated balance.');
            fetchWallet();
            fetchTransactions();
          }
          return false;
        }
      };
      
      verifyPayment();
      // Clean URL
      navigate('/provider/earnings', { replace: true });
    } else if (paymentStatus === 'cancelled') {
      const cancelTxnId = searchParams.get('txn_id');
      // Mark the transaction as cancelled in the backend
      if (cancelTxnId) {
        axios.post(`${BACKEND_URL}/api/wallet/topup/cancel/${cancelTxnId}`, {}, { headers })
          .then(() => {
            fetchTransactions(); // Refresh transactions list
          })
          .catch(() => {});
      }
      toast.error('Payment was cancelled');
      navigate('/provider/earnings', { replace: true });
    } else {
      // No specific payment status - cancel any pending transactions
      cancelPendingTransactions();
    }
  }, [searchParams, navigate, token]);

  const selectedBank = banks.find(b => b.id === payoutData.payout_method);
  const minWithdraw = withdrawalConfig.minimum_amount || 500;
  const maxWithdraw = withdrawalConfig.maximum_amount || 50000;

  const handlePayout = async () => {
    const amount = parseFloat(payoutData.amount);
    if (!amount || amount < minWithdraw) {
      toast.error(`Minimum payout is ₱${minWithdraw.toLocaleString()}`);
      return;
    }
    if (amount > (wallet?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }
    if (!payoutData.payout_method) {
      toast.error('Please select a bank');
      return;
    }
    if (!payoutData.recipient_identifier) {
      toast.error('Please enter account details');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/wallet/payout`, {
        amount: amount,
        payout_method: payoutData.payout_method,
        recipient_identifier: payoutData.recipient_identifier,
        account_name: payoutData.account_name
      }, { headers });
      toast.success('Withdrawal request submitted! Status: Pending');
      setShowPayoutDialog(false);
      setPayoutData({ amount: '', payout_method: banks[0]?.id || '', recipient_identifier: '', account_name: '' });
      fetchWallet();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-PH', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `₱${Number(amount || 0).toFixed(2)}`;
  };

  // Calculate earnings stats from current page (for display purposes)
  const earnings = transactions.filter(t => t.type === 'booking_earnings');
  const totalEarningsPage = earnings.reduce((sum, t) => sum + (t.amount || 0), 0);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700',
      expired: 'bg-slate-100 text-slate-700'
    };
    const icons = {
      pending: Clock,
      processing: Loader2,
      completed: CheckCircle,
      rejected: XCircle,
      cancelled: XCircle,
      failed: XCircle,
      expired: XCircle
    };
    const Icon = icons[status] || Clock;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-20 px-4 rounded-b-3xl">
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate('/provider')}
            className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white font-[Poppins]">Earnings</h1>
        </div>
        
        {/* Balance */}
        <div className="text-center">
          <p className="text-slate-400 text-sm mb-1">Available Balance</p>
          <p className={`text-4xl font-bold mb-2 ${(wallet?.available_balance ?? wallet?.balance) <= 0 ? 'text-red-400' : 'text-white'}`}>
            {isLoadingWallet ? '...' : formatAmount(wallet?.available_balance ?? wallet?.balance ?? 0)}
          </p>
          
          {/* Locked Funds Indicator */}
          {lockedFunds && lockedFunds.total_locked > 0 && (
            <div 
              className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/30 rounded-full px-4 py-1.5 mb-4 cursor-pointer"
              onClick={() => setShowLockedFundsInfo(true)}
              data-testid="locked-funds-indicator"
            >
              <Shield className="w-4 h-4 text-amber-400" />
              <span className="text-amber-200 text-sm">
                {formatAmount(lockedFunds.total_locked)} locked (warranty)
              </span>
              <Info className="w-3.5 h-3.5 text-amber-400" />
            </div>
          )}
          
          <div className="flex items-center justify-center gap-3">
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-12 px-6"
              onClick={() => setShowPayoutDialog(true)}
              disabled={!withdrawalConfig.enabled || !(wallet?.available_balance ?? wallet?.balance) || (wallet?.available_balance ?? wallet?.balance) < minWithdraw}
              data-testid="withdraw-btn"
            >
              <ArrowUpRight className="w-5 h-5 mr-2" />
              Withdraw
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600 text-white h-12 px-6"
              onClick={() => setShowTopupDialog(true)}
              data-testid="topup-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Top Up
            </Button>
          </div>
          {(wallet?.available_balance ?? wallet?.balance) > 0 && (wallet?.available_balance ?? wallet?.balance) < minWithdraw && (
            <p className="text-amber-200 text-xs mt-2">Minimum ₱{minWithdraw.toLocaleString()} required for withdrawal</p>
          )}
          {(wallet?.available_balance ?? wallet?.balance) <= 0 && (
            <p className="text-red-300 text-xs mt-2">⚠️ Your available balance is low. Top up to continue accepting bookings.</p>
          )}
        </div>
      </header>

      <main className="px-4 -mt-8">
        {/* Account Status Warnings */}
        <AccountStatusBanner />
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className="text-slate-500 text-sm">Transactions</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{transactionsTotal}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <WalletIcon className="w-5 h-5 text-blue-500" />
              <span className="text-slate-500 text-sm">Balance</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{formatAmount(wallet?.balance || 0)}</p>
          </div>
        </div>

        {/* Transaction History */}
        <section className="mb-24">
          <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">Transaction History</h3>
          
          {isLoadingTransactions ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-stone-200 rounded-xl h-20" />
              ))}
            </div>
          ) : transactions.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {transactions.map((tx, idx) => (
                <div 
                  key={tx.id}
                  className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                    idx > 0 ? 'border-t border-stone-100' : ''
                  }`}
                  onClick={() => fetchTransactionDetail(tx.id)}
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        (tx.type === 'booking_earnings' || tx.type === 'booking_earning') ? 'bg-green-100' : 
                        tx.type === 'topup' ? 'bg-green-100' :
                        tx.type === 'admin_adjustment' ? (tx.amount >= 0 ? 'bg-green-100' : 'bg-red-100') :
                        tx.type === 'commission_deduction' ? 'bg-amber-100' :
                        tx.type === 'payout' ? 'bg-red-100' : 'bg-slate-100'
                      }`}>
                        {(tx.type === 'booking_earnings' || tx.type === 'booking_earning') ? (
                          <ArrowDownLeft className="w-5 h-5 text-green-600" />
                        ) : tx.type === 'topup' ? (
                          <Plus className="w-5 h-5 text-green-600" />
                        ) : tx.type === 'admin_adjustment' ? (
                          tx.amount >= 0 ? <Plus className="w-5 h-5 text-green-600" /> : <ArrowUpRight className="w-5 h-5 text-red-600" />
                        ) : tx.type === 'commission_deduction' ? (
                          <CreditCard className="w-5 h-5 text-amber-600" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 capitalize">
                          {tx.type === 'booking_earnings' || tx.type === 'booking_earning' ? 'Job Earnings' :
                           tx.type === 'topup' ? 'Wallet Top-up' :
                           tx.type === 'admin_adjustment' ? 'HandyBayani Adjustment' :
                           tx.type === 'commission_deduction' ? 'Platform Commission' :
                           tx.type === 'payout' ? 'Withdrawal' : tx.type.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-slate-500">{formatDate(tx.created_at)}</p>
                        {tx.type === 'payout' && tx.bank && (
                          <p className="text-xs text-slate-400 mt-0.5">{tx.bank.name}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${
                        (tx.type === 'booking_earnings' || tx.type === 'booking_earning' || tx.type === 'topup') 
                          ? 'text-green-600' 
                          : tx.type === 'admin_adjustment' 
                            ? (tx.amount >= 0 ? 'text-green-600' : 'text-red-600')
                            : 'text-red-600'
                      }`}>
                        {(tx.type === 'booking_earnings' || tx.type === 'booking_earning' || tx.type === 'topup') 
                          ? '+' 
                          : tx.type === 'admin_adjustment'
                            ? (tx.amount >= 0 ? '+' : '-')
                            : '-'}{formatAmount(Math.abs(tx.amount))}
                      </p>
                      <div className="mt-1">
                        {getStatusBadge(tx.status)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Show Batch ID for payouts */}
                  {tx.type === 'payout' && tx.batch_id && (
                    <div className="mt-2 flex items-center gap-2">
                      <Package className="w-3 h-3 text-slate-400" />
                      <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                        {tx.batch_id}
                      </span>
                    </div>
                  )}
                  
                  {/* View details hint */}
                  <div className="mt-2 flex items-center gap-1 text-xs text-blue-500">
                    <Eye className="w-3 h-3" />
                    <span>Tap to view details</span>
                  </div>
                </div>
              ))}
              
              {/* Pagination */}
              <TablePagination
                page={page}
                totalPages={totalPages}
                total={transactionsTotal}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </div>
          ) : (
            <div className="text-center py-12 bg-stone-50 rounded-2xl">
              <WalletIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">No transactions yet</p>
              <p className="text-sm text-slate-400">Complete jobs to earn money</p>
            </div>
          )}
        </section>
      </main>

      {/* Bottom Navigation */}
      <ProviderBottomNav />

      {/* Transaction Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {isLoadingDetail ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            </div>
          ) : selectedTransaction ? (
            <div className="space-y-4 mt-4">
              {/* Amount & Type */}
              <div className={`text-center py-4 rounded-xl ${
                (selectedTransaction.type === 'booking_earnings' || selectedTransaction.type === 'booking_earning' || selectedTransaction.type === 'topup')
                  ? 'bg-green-50' 
                  : selectedTransaction.type === 'admin_adjustment'
                    ? (selectedTransaction.amount >= 0 ? 'bg-green-50' : 'bg-red-50')
                    : 'bg-red-50'
              }`}>
                <p className={`text-3xl font-bold ${
                  (selectedTransaction.type === 'booking_earnings' || selectedTransaction.type === 'booking_earning' || selectedTransaction.type === 'topup')
                    ? 'text-green-600' 
                    : selectedTransaction.type === 'admin_adjustment'
                      ? (selectedTransaction.amount >= 0 ? 'text-green-600' : 'text-red-600')
                      : 'text-red-600'
                }`}>
                  {(selectedTransaction.type === 'booking_earnings' || selectedTransaction.type === 'booking_earning' || selectedTransaction.type === 'topup') 
                    ? '+' 
                    : selectedTransaction.type === 'admin_adjustment'
                      ? (selectedTransaction.amount >= 0 ? '+' : '-')
                      : '-'}
                  {formatAmount(Math.abs(selectedTransaction.amount))}
                </p>
                <p className="text-sm text-slate-500 capitalize mt-1">
                  {selectedTransaction.type === 'booking_earnings' || selectedTransaction.type === 'booking_earning' 
                    ? 'Job Earnings' 
                    : selectedTransaction.type === 'topup'
                      ? 'Wallet Top-up'
                      : selectedTransaction.type === 'admin_adjustment'
                        ? 'HandyBayani Adjustment'
                        : selectedTransaction.type === 'commission_deduction'
                          ? 'Platform Commission'
                          : 'Withdrawal Request'}
                </p>
              </div>
              
              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <span className="text-sm text-slate-500">Status</span>
                {getStatusBadge(selectedTransaction.status)}
              </div>
              
              {/* Reference */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <span className="text-sm text-slate-500">Reference</span>
                <span className="text-sm font-mono font-medium">{selectedTransaction.reference || selectedTransaction.id?.slice(0, 8).toUpperCase()}</span>
              </div>
              
              {/* Date */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <span className="text-sm text-slate-500">Date</span>
                <span className="text-sm font-medium">{formatDate(selectedTransaction.created_at)}</span>
              </div>
              
              {/* Admin Adjustment Reason */}
              {selectedTransaction.type === 'admin_adjustment' && selectedTransaction.description && (
                <div className="p-3 bg-amber-50 rounded-xl">
                  <span className="text-sm text-amber-700 font-medium block mb-1">Adjustment Reason</span>
                  <p className="text-sm text-slate-700">
                    {selectedTransaction.description.replace('HandyBayani adjustment: ', '')}
                  </p>
                </div>
              )}
              
              {/* Payout specific details */}
              {selectedTransaction.type === 'payout' && (
                <>
                  {/* Bank Info */}
                  {selectedTransaction.bank && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="text-sm text-slate-500">Bank</span>
                      <span className="text-sm font-medium">{selectedTransaction.bank.name}</span>
                    </div>
                  )}
                  
                  {/* Account Number */}
                  {selectedTransaction.recipient_identifier && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="text-sm text-slate-500">Account Number</span>
                      <span className="text-sm font-mono">{selectedTransaction.recipient_identifier}</span>
                    </div>
                  )}
                  
                  {/* Account Name */}
                  {selectedTransaction.account_name && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="text-sm text-slate-500">Account Name</span>
                      <span className="text-sm font-medium">{selectedTransaction.account_name}</span>
                    </div>
                  )}
                  
                  {/* Batch ID */}
                  {selectedTransaction.batch_id && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                      <span className="text-sm text-slate-500">Batch ID</span>
                      <span className="text-sm font-mono font-medium text-blue-700">{selectedTransaction.batch_id}</span>
                    </div>
                  )}
                  
                  {/* Batch Date */}
                  {selectedTransaction.batch_date && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                      <span className="text-sm text-slate-500">Batch Date</span>
                      <span className="text-sm font-medium text-blue-700">{formatDate(selectedTransaction.batch_date)}</span>
                    </div>
                  )}
                </>
              )}
              
              <Button
                variant="outline"
                onClick={() => setShowDetailDialog(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw Funds</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-sm text-green-800">Available Balance</p>
              <p className="text-2xl font-bold text-green-600">{formatAmount(wallet?.balance || 0)}</p>
            </div>
            
            <div>
              <Label>Amount to Withdraw</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₱</span>
                <Input
                  type="number"
                  placeholder="0"
                  min={minWithdraw}
                  max={Math.min(maxWithdraw, wallet?.balance || 0)}
                  value={payoutData.amount}
                  onChange={(e) => setPayoutData(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-14 pl-8 text-2xl font-bold"
                  data-testid="payout-amount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Min: ₱{minWithdraw.toLocaleString()} | Max: ₱{Math.min(maxWithdraw, wallet?.balance || 0).toLocaleString()}
                {withdrawalConfig.processing_fee > 0 && (
                  <span className="ml-2">| Fee: {withdrawalConfig.processing_fee_type === 'fixed' ? `₱${withdrawalConfig.processing_fee}` : `${withdrawalConfig.processing_fee}%`}</span>
                )}
              </p>
            </div>
            
            <div>
              <Label>Select Bank / E-Wallet</Label>
              <div className="relative mt-1.5">
                <select
                  value={payoutData.payout_method}
                  onChange={(e) => setPayoutData(prev => ({ 
                    ...prev, 
                    payout_method: e.target.value,
                    recipient_identifier: '',
                    account_name: ''
                  }))}
                  className="w-full h-12 border border-stone-200 rounded-xl px-4 pr-10 appearance-none bg-white text-slate-900 font-medium"
                  data-testid="bank-select"
                >
                  {banks.length === 0 && <option value="">No banks available</option>}
                  {banks.map(bank => (
                    <option key={bank.id} value={bank.id}>{bank.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>
            
            <div>
              <Label>{selectedBank?.account_format || 'Account Number'}</Label>
              <Input
                placeholder={selectedBank?.account_format || 'Enter account number'}
                value={payoutData.recipient_identifier}
                onChange={(e) => setPayoutData(prev => ({ ...prev, recipient_identifier: e.target.value }))}
                className="h-12 mt-1.5"
                data-testid="payout-account-input"
              />
            </div>
            
            <div>
              <Label>Account Name (Optional)</Label>
              <Input
                placeholder="Account holder name"
                value={payoutData.account_name}
                onChange={(e) => setPayoutData(prev => ({ ...prev, account_name: e.target.value }))}
                className="h-12 mt-1.5"
                data-testid="payout-name-input"
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Your withdrawal request will be submitted with <strong>Pending</strong> status and will be processed by the admin.
              </p>
            </div>
            
            <Button
              onClick={handlePayout}
              disabled={isSubmitting || !payoutData.amount || !payoutData.payout_method}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              data-testid="submit-payout-btn"
            >
              {isSubmitting ? 'Processing...' : 'Submit Withdrawal Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Top-up Dialog for Providers */}
      <Dialog open={showTopupDialog} onOpenChange={setShowTopupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Top Up Wallet</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-sm text-blue-800">
                Top up your wallet to ensure you can continue accepting bookings. 
                When you receive cash payments, the platform commission is deducted from your wallet balance.
              </p>
            </div>
            
            <div>
              <Label>Top-up Amount (PHP)</Label>
              <Input
                type="number"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="Enter amount (min ₱100)"
                min="100"
                className="mt-1"
                data-testid="topup-amount-input"
              />
            </div>
            
            {/* Quick amount buttons */}
            <div className="grid grid-cols-4 gap-2">
              {[500, 1000, 2000, 5000].map(amount => (
                <button
                  key={amount}
                  onClick={() => setTopupAmount(amount.toString())}
                  className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    topupAmount === amount.toString()
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  ₱{amount.toLocaleString()}
                </button>
              ))}
            </div>
            
            <Button
              onClick={async () => {
                const minAmount = walletSettings.min_topup_amount || 100;
                if (!topupAmount || Number(topupAmount) < minAmount) {
                  toast.error(`Minimum top-up is ₱${minAmount}`);
                  return;
                }
                setIsTopupLoading(true);
                try {
                  const res = await axios.post(`${BACKEND_URL}/api/wallet/topup`, {
                    amount: Number(topupAmount),
                    payment_method: 'gcash'
                  }, { headers });
                  
                  if (res.data.checkout_url) {
                    // Redirect through payment page to prevent navigation issues
                    const transactionId = res.data.transaction_id || '';
                    const checkoutUrl = encodeURIComponent(res.data.checkout_url);
                    const returnUrl = encodeURIComponent('/provider/earnings');
                    navigate(`/payment?checkout_url=${checkoutUrl}&transaction_id=${transactionId}&return_url=${returnUrl}&user_type=provider`);
                  } else {
                    toast.success('Top-up successful!');
                    setShowTopupDialog(false);
                    setTopupAmount('');
                    fetchWallet();
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Top-up failed');
                } finally {
                  setIsTopupLoading(false);
                }
              }}
              disabled={isTopupLoading || !topupAmount || Number(topupAmount) < (walletSettings.min_topup_amount || 100)}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
              data-testid="submit-topup-btn"
            >
              {isTopupLoading ? 'Processing...' : `Top Up ₱${Number(topupAmount || 0).toLocaleString()}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Locked Funds Info Dialog */}
      <Dialog open={showLockedFundsInfo} onOpenChange={setShowLockedFundsInfo}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Locked Funds (Warranty)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800">
                <strong>Why are funds locked?</strong>
              </p>
              <p className="text-sm text-amber-700 mt-1">
                For services with warranty, customer payments are held until the warranty period ends. 
                This protects customers and ensures service quality.
              </p>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-slate-600">Total Locked</span>
                <span className="text-lg font-bold text-amber-600">{formatAmount(lockedFunds?.total_locked || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Active Warranties</span>
                <span className="text-sm font-medium text-slate-900">{lockedFunds?.bookings_count || 0}</span>
              </div>
            </div>

            {lockedFunds?.locked_bookings?.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-900">Warranty Details</p>
                {lockedFunds.locked_bookings.map((booking) => (
                  <div key={booking.booking_id} className="bg-stone-50 rounded-xl p-3 border border-stone-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{booking.service_name}</p>
                        <p className="text-xs text-slate-500">#{booking.booking_ref}</p>
                      </div>
                      <span className="text-sm font-bold text-green-600">{formatAmount(booking.amount_locked)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1">
                      <Clock className="w-3 h-3" />
                      <span>
                        {booking.remaining_days > 0 
                          ? `${booking.remaining_days}d ${booking.remaining_hours}h remaining`
                          : `${booking.remaining_hours}h remaining`
                        }
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-slate-500 text-center">
              Funds will be automatically released when warranty expires
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
